interface SectionDividerProps {
  variant?: 'default' | 'gradient' | 'wave' | 'dots' | 'geometric' | 'icon';
  icon?: string;
}

export default function SectionDivider({ variant = 'default', icon }: SectionDividerProps) {
  // Gradient line with glow
  if (variant === 'gradient') {
    return (
      <div className="relative py-8">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full h-px bg-gradient-to-r from-transparent via-[#FF6B6B] to-transparent"></div>
        </div>
        <div className="absolute inset-0 flex items-center">
          <div className="w-full h-2 bg-gradient-to-r from-transparent via-[#FF6B6B]/20 to-transparent blur-sm"></div>
        </div>
      </div>
    );
  }

  // Wave SVG divider
  if (variant === 'wave') {
    return (
      <div className="relative w-full h-32">
        <svg
          className="absolute inset-0 w-full h-full"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1440 320"
          preserveAspectRatio="none"
        >
          <path
            fill="white"
            fillOpacity="1"
            d="M0,96L48,112C96,128,192,160,288,160C384,160,480,128,576,112C672,96,768,96,864,112C960,128,1056,160,1152,165.3C1248,171,1344,149,1392,138.7L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
          />
        </svg>
      </div>
    );
  }

  // Dots pattern
  if (variant === 'dots') {
    return (
      <div className="py-12 flex items-center justify-center">
        <div className="flex items-center gap-2">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className={`rounded-full transition-all duration-300 ${
                i === 2
                  ? 'w-3 h-3 bg-gradient-to-r from-[#FF6B6B] to-[#FF8E53]'
                  : 'w-2 h-2 bg-gray-300'
              }`}
            />
          ))}
        </div>
      </div>
    );
  }

  // Geometric shapes
  if (variant === 'geometric') {
    return (
      <div className="py-16 flex items-center justify-center">
        <div className="flex items-center gap-6">
          <div className="w-12 h-px bg-gradient-to-r from-transparent to-[#FF6B6B]"></div>
          <div className="w-4 h-4 rotate-45 border-2 border-[#FF6B6B] bg-white"></div>
          <div className="w-3 h-3 rounded-full bg-gradient-to-r from-[#FF6B6B] to-[#FF8E53]"></div>
          <div className="w-4 h-4 rotate-45 border-2 border-[#FF8E53] bg-white"></div>
          <div className="w-12 h-px bg-gradient-to-l from-transparent to-[#FF8E53]"></div>
        </div>
      </div>
    );
  }

  // Icon divider
  if (variant === 'icon' && icon) {
    return (
      <div className="py-12 flex items-center justify-center">
        <div className="flex items-center gap-4 w-full max-w-2xl">
          <div className="flex-1 h-px bg-gradient-to-r from-transparent to-gray-300"></div>
          <div className="p-3 rounded-full bg-gradient-to-br from-[#FF6B6B] to-[#FF8E53] text-white shadow-lg">
            <i className={`${icon} text-xl`}></i>
          </div>
          <div className="flex-1 h-px bg-gradient-to-l from-transparent to-gray-300"></div>
        </div>
      </div>
    );
  }

  // Default: Enhanced dots with lines
  return (
    <div className="py-12 flex items-center justify-center">
      <div className="flex items-center gap-4 w-full max-w-md">
        <div className="flex-1 h-px bg-gradient-to-r from-transparent to-gray-300"></div>
        <div className="w-2 h-2 rounded-full bg-[#FF6B6B] animate-pulse"></div>
        <div className="w-3 h-3 rounded-full bg-gradient-to-r from-[#FF6B6B] to-[#FF8E53] shadow-lg"></div>
        <div className="w-2 h-2 rounded-full bg-[#FFA07A] animate-pulse"></div>
        <div className="flex-1 h-px bg-gradient-to-l from-transparent to-gray-300"></div>
      </div>
    </div>
  );
}
